/* eslint-disable max-lines */
import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { ListAspectSchema } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import type { FilterEditorOptions } from '@adornis/filter/filter-options.js';
import '@adornis/popover/x-tooltip.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import '@chancenlogistik/client/components/logistik-component-base-page-layout';
import {
  g_getProductCategoryString,
  g_getShippingStatusColor,
  g_getShippingStatusIcon,
} from '@chancenlogistik/client/helper.js';
import { LogistikRoutes } from '@chancenlogistik/client/routes.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import { ellipsis } from '@chancenwerk/client/util.js';
import {
  LogistikEntireTrip,
  LogistikIndividualOrderItem,
  LogistikLearningMaterialOrderItem,
  LogistikOrder,
  LogistikOrderChancenBriefItem,
  LogistikOrderClearanceState,
  LogistikOrderDeliveryState,
  type LogistikOrderItem,
  LogistikOtherOrder,
  displayLogistikOrderID,
} from '@chancenwerk/db/LogistikOrder.js';
import { AdvertisingMaterial, ChancenBrief, LearningMaterial } from '@chancenwerk/db/LogistikProduct.js';
import { Adresse } from '@chancenwerk/db/adresse.js';
import { LogistikOrderAPI } from '@chancenwerk/db/api/LogistikOrderAPI.js';
import {
  LogistikProductCategory,
  LogistikShippingRecipient,
  LogistikShippingStatus,
} from '@chancenwerk/db/enums/LogistikEnums.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { LogistikOrderFilter } from '@chancenwerk/db/filter/LogistikOrderFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { ChancenRoleAspect } from '@chancenwerk/db/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-loader.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-tabs.js';
import '@chancenwerk/design/components/cr-textarea.js';
import '@chancenwerk/design/components/cr-tooltip.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { ChancenwerkUserAspectSchema } from '@chancenwerk/master-data-management/users/entities/aspects/ChancenwerkUserAspect.js';
import { ChancenPersonalConfigAPI } from '@chancenwerk/user-configs/api/ChancenPersonalConfigAPI.js';
import { ListUIConfig } from '@chancenwerk/user-configs/entities/ListUIConfig.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { filter, firstValueFrom, switchMap, take, takeUntil, timer } from 'rxjs';
import { LogistikDialogAddManualOrder } from '../components/dialogs/logistik-dialog-add-manual-order.js';
import { LogistikDialogDeliveryNotices } from '../components/dialogs/logistik-dialog-delivery-notices.js';
import { LogistikDialogEditOrder } from '../components/dialogs/logistik-dialog-edit-order.js';
import { LogistikDialogMergeOrders } from '../components/dialogs/logistik-dialog-merge-orders.js';
import { LogistikDialogOrderReminder } from '../components/dialogs/logistik-dialog-order-reminder.js';
import { LogistikDialogSendCustomerEmail } from '../components/dialogs/logistik-dialog-send-customer-email.js';
import {
  acceptIndividualOrder,
  declineIndividualOrder,
} from '../components/dialogs/logistik-dialogs-accept-decline-invidivdual-order.js';

enum Tabs {
  CURRENT_ORDERS = 'Aktuelle Bestellungen',
  COMPLETED_ORDERS = 'Abgeschlossene Bestellungen',
  AWAITS_REVIEW = 'Wartet auf Freigabe',
  DECLINED = 'Freigabe abgelehnt',
}

const fieldsWhitelist = {
  // Empfänger:in
  empfaenger: 1,
  // Frist
  frist: 1,
  // Kund:in
  client: 1,
  // Status
  status: 1,
  // Produkt
  productCategory: 1,
  // Bestellnummer
  orderNumber: 1,
  // Bearbeiter:in
  bearbeiter: 1,
  // Bestelldatum
  bestelldatum: 1,
  // Auftraggeber:in
  auftraggeber: 1,
  // Ist Manuelle Bestellung?
  isManuallyCreated: 1,
  // Preis
  price: 1,
  // Freigabeaktionen
  freigabeaktionen: 1,
  // Zugangs-Token
  accessToken: 1,
} as const;

// region Lit Element
/**
 * @customElement logistik-page-terminal
 */
@customElement('logistik-page-terminal')
export class LogistikPageTerminal extends ChemistryLitElement {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _selectedTab = new RXController(this, Tabs.CURRENT_ORDERS);

  @state() private readonly _selectedItems = new Set<LogistikOrder>();

  @state() private readonly _config = new RXController(this, this._currentUser.observable, obs =>
    obs.pipe(
      switchMap(async u => {
        if (!u) return null;
        return ChancenPersonalConfigAPI.getByKeyAndCategory(
          ListUIConfig<typeof LogistikOrder>,
          'list-orders',
          u._id,
        )({
          key: 1,
          userID: 1,
          category: 1,
          view: {
            filter: LogistikOrderFilter.allFields,
            mode: 1,
            configuredColumns: {
              customID: 1,
              fieldName: 1,
              columnWidth: 1,
            },
            selectedItems: 1,
            multiSelection: 1,
          },
        });
      }),
    ),
  );

  private updateConfig(t: Tabs) {
    if (!(this._config.value?.view?.filter instanceof LogistikOrderFilter)) return;
    const { status, needsClearance, declinedClearance } = ((tab: Tabs) => {
      switch (tab) {
        case Tabs.CURRENT_ORDERS:
          return {
            status: [
              LogistikShippingStatus.NEW,
              LogistikShippingStatus.PROBLEM,
              LogistikShippingStatus.PROCESSING,
              LogistikShippingStatus.SHIPPED,
              LogistikShippingStatus.FORWARDED,
            ],

            needsClearance: false,
            declinedClearance: false,
          };
        case Tabs.COMPLETED_ORDERS:
          return {
            status: [LogistikShippingStatus.COMPLETED],
            needsClearance: false,
            declinedClearance: false,
          };
        case Tabs.AWAITS_REVIEW:
          return {
            status: null,
            needsClearance: true,
            declinedClearance: false,
          };
        case Tabs.DECLINED:
          return {
            status: null,
            needsClearance: false,
            declinedClearance: true,
          };
      }
    })(t);
    this._config.value.view.filter.status = status;
    this._config.value.view.filter.needsClearance = needsClearance;
    this._config.value.view.filter.declinedClearance = declinedClearance;
    this._config.value.view.configuredColumns = ((
      tab: Tabs,
      columns: Maybe<FieldConfiguration[]>,
    ): Maybe<FieldConfiguration[]> => {
      if (!columns) return columns;
      switch (tab) {
        case Tabs.CURRENT_ORDERS: // ! fallthrough
        case Tabs.COMPLETED_ORDERS:
          return columns.filter(c => c.customID !== 'price' && c.customID !== 'freigabeaktionen');
        case Tabs.AWAITS_REVIEW:
          if (!columns.find(c => c.customID === 'freigabeaktionen'))
            columns.push(new FieldConfiguration({ customID: 'freigabeaktionen' }));
          if (!columns.find(c => c.customID === 'price')) columns.push(new FieldConfiguration({ customID: 'price' }));
          return columns;
        case Tabs.DECLINED:
          if (!columns.find(c => c.customID === 'price')) columns.push(new FieldConfiguration({ customID: 'price' }));
          return columns.filter(c => c.customID !== 'freigabeaktionen');
        default:
          break;
      }
    })(t, this._config.value.view.configuredColumns);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this._config.observable.pipe(filter(Boolean), take(1)).subscribe(c => {
      if (c.view?.filter && c.view.filter instanceof LogistikOrderFilter) {
        c.view.filter.status = [
          LogistikShippingStatus.NEW,
          LogistikShippingStatus.PROBLEM,
          LogistikShippingStatus.PROCESSING,
          LogistikShippingStatus.SHIPPED,
          LogistikShippingStatus.FORWARDED,
        ];
        c.view.filter.needsClearance = false;
        c.view.filter.declinedClearance = false;
      }
    });
    this._selectedTab.observable.pipe(takeUntil(this.disconnected)).subscribe(t => this.updateConfig(t));
  }

  override render() {
    return permissionGuard(ChancenPermission.logistik.LOGIN_LOGISTIK, () => this._content(), {
      forbiddenTemplate: html`
        <logistik-component-base-page-layout>
          <cr-flex ${css({ height: '100%' })} center crossaxis-center>
            <campus-page-no-permission></campus-page-no-permission>
          </cr-flex>
        </logistik-component-base-page-layout>
      `,
      context: ANY_CONTEXT,
    });
  }
  private _content() {
    return html`
      <logistik-component-base-page-layout no-section>
        <logistik-component-base-header pageTitle="Terminal" backRoute=${LogistikRoutes.landing}>
          <x-flex horizontal crossaxis-center space="md">
            <cr-button mode="text" icon="add" @click=${() => LogistikDialogAddManualOrder.showPopup()}>
              Manuelle Bestellung anlegen
            </cr-button>
            <cr-button icon="receipt" @click=${() => LogistikDialogDeliveryNotices.showPopup({})}>
              Empfangsbestätigungen
            </cr-button>
          </x-flex>
        </logistik-component-base-header>
        ${this._tabs()}
        ${this._selectedItems.size
          ? html`
              <x-flex horizontal end crossaxis-center space="md">
                <cr-button
                  mode="text"
                  icon="close"
                  @click=${() => {
                    this._selectedItems.clear();
                    this.requestUpdate();
                  }}
                >
                  Auswahl zurücksetzen
                </cr-button>
                <cr-button
                  icon="merge"
                  @click=${() =>
                    LogistikDialogMergeOrders.showPopup({ props: { orders: Array.from(this._selectedItems) } })}
                >
                  Ausgewählte Bestellungen (${this._selectedItems.size}) zusammenfassen
                </cr-button>
              </x-flex>
            `
          : nothing}
        ${this._dataView()}
      </logistik-component-base-page-layout>
    `;
  }

  private _tabs(): Renderable {
    return html`
      <cr-tabs
        .selected=${this._selectedTab.value}
        ${css({ borderBottom: `1px solid ${this.colors.neutral.shade(300)}` })}
      >
        ${repeat(
          Object.values(Tabs),
          (_t, i) => i,
          t => html` <cr-tab label=${t} @click=${() => this._selectedTab.next(t)}> ${t} </cr-tab>`,
        )}
      </cr-tabs>
    `;
  }

  private _dataView(): Renderable {
    if (this._config.isLoading) return html` <cr-loader></cr-loader> `;
    const dataViewOptions: DataViewOptions<typeof LogistikOrder> = {
      class: LogistikOrder,
      operation: LogistikOrderAPI.subscribePaginated,
      emptyState: () => this._getEmptyState(),
      selectionSet: orderSelector,
      isColumnResortable: true,
      isSortable: true,
      isColumnSelectable: true,
      onlyShowLastTranslation: true,
      customColumns: this._columnDefinitions,
      view:
        this._config.value?.view ??
        new View({
          filter: new LogistikOrderFilter({}),
          selectedItems: [...this._selectedItems].map(o => o._id),
        }),
      onViewChanged: async view => {
        if (!this._config.value) return;
        this._config.value.view = view;
        if (this._config.value._id) await this._config.value.save();
        else await this._config.value.create();
      },
      fieldSelectionOptions: {
        hideFieldsWithoutTranslation: true,
        shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({
          whitelist: fieldsWhitelist,
        }),
      },
    };
    const filterOptions: Omit<FilterEditorOptions<typeof LogistikOrder>, 'filter' | 'class'> = {
      showAspectsPicker: true,
      fieldAspectGeneration: {
        hideFieldsWithoutTranslation: true,
        whitelist: fieldsWhitelist,
      },
    };
    const options = buildChancenDataViewOptions(dataViewOptions, filterOptions);
    return html`
      <cr-card padding="sm" margin="sm" ${css({ flexShrink: '0', position: 'sticky', left: 0 })}>
        <x-filter-editor .options=${options.filterOptions}> </x-filter-editor>
      </cr-card>

      <cr-data-view .options=${options.dataViewOptions}> </cr-data-view>
    `;
  }

  private _getEmptyState() {
    return html` <cr-text>Es wurden noch keine Items hinzugefügt. Hier gibt es nichts zu sehen :(</cr-text> `;
  }

  // region Columns
  private readonly checkboxColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnWidth: 'max-content',
    customID: '_selection',
    isSticky: true,
    position: 'front',
    isSortable: false,
    isAlwaysVisible: true,
    renderItem: entityInstance =>
      entityInstance.status !== LogistikShippingStatus.SHIPPED &&
      !entityInstance.items.some(item => item instanceof LogistikIndividualOrderItem)
        ? html`
            <cr-checkbox
              @value-picked=${(e: ValueEvent<boolean>) => {
                if (e.detail.value) this._selectedItems.add(entityInstance);
                else this._selectedItems.delete(entityInstance);
                this.requestUpdate('_selectedItems', new Set());
              }}
              .value=${this._selectedItems.has(entityInstance)}
            ></cr-checkbox>
          `
        : nothing,
  };

  private readonly splitOrderColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: '',
    fieldName: 'splitOrderIDs',
    columnWidth: 'min-content',
    isSortable: false,
    isSticky: true,
    position: 'front',
    isAlwaysVisible: true,
    renderItem: entityInstance => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      if (entityInstance.splitOrderIDs?.length || entityInstance.splitFrom) {
        return html` <cr-flex center>
          <cr-icon ${css({ fontSize: '1.2rem', color: 'orange' })}>
            call_split <cr-tooltip .text=${html`<cr-text>Teilbestellung</cr-text>`}></cr-tooltip>
          </cr-icon>
        </cr-flex>`;
      } else if (entityInstance.mergeOriginalOrders?.length) {
        return html` <cr-flex center>
          <cr-icon ${css({ fontSize: '1.2rem', color: 'orange' })}>
            merge <cr-tooltip .text=${html`<cr-text>Zusammengefasste Bestellung</cr-text>`}></cr-tooltip>
          </cr-icon>
        </cr-flex>`;
      } else {
        return nothing;
      }
    },
  };

  private readonly emailKnownWarningColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: '',
    fieldName: 'shippingDetails.isEmailKnown',
    columnWidth: 'min-content',
    isSortable: false,
    isAlwaysVisible: true,
    isSticky: true,
    position: 'front',
    renderItem: entityInstance => {
      if (!entityInstance.shippingDetails.isEmailKnown && !entityInstance.accessTokenValid) {
        return html` <cr-flex center>
          <cr-icon ${css({ fontSize: '1.2rem', color: 'orange' })}>
            warning <cr-tooltip .text=${html`<cr-text>E-Mail unbekannt</cr-text>`}></cr-tooltip>
          </cr-icon>
        </cr-flex>`;
      } else {
        return nothing;
      }
    },
  };

  private readonly isDigitalColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: '',
    customID: '_isDigital',
    columnWidth: 'min-content',
    isSortable: false,
    isAlwaysVisible: true,
    isSticky: true,
    position: 'front',
    renderItem: entityInstance => {
      if (
        entityInstance.items.some(
          item =>
            item instanceof LogistikLearningMaterialOrderItem && (item as LogistikLearningMaterialOrderItem).isDigital,
        )
      ) {
        return html` <cr-flex center>
          <cr-icon ${css({ fontSize: '1.2rem', color: this.colors.accent })}>
            picture_as_pdf
            <cr-tooltip>Enthält digitale Produkte</cr-tooltip>
          </cr-icon>
        </cr-flex>`;
      } else {
        return nothing;
      }
    },
  };

  private readonly isSelfprintColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: '',
    customID: '_isSelfprint',
    columnWidth: 'min-content',
    isSortable: false,
    isAlwaysVisible: true,
    isSticky: true,
    position: 'front',
    renderItem: entityInstance => {
      if (
        entityInstance.items.some(
          item => item instanceof LogistikOrderChancenBriefItem && (item as LogistikOrderChancenBriefItem).isSelfPrint,
        )
      ) {
        return html` <cr-flex center>
          <cr-icon ${css({ fontSize: '1.2rem', color: this.colors.accent })}>
            print
            <cr-tooltip>Enthält Selbstdruckprodukte</cr-tooltip>
          </cr-icon>
        </cr-flex>`;
      } else {
        return nothing;
      }
    },
  };

  private readonly notesColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnWidth: 'max-content',
    customID: '_notes',
    isSortable: false,
    isAlwaysVisible: true,
    position: 'front',
    isSticky: true,
    renderItem: entityInstance =>
      html`
        <cr-icon
          ${css({
            cursor: 'pointer',
            ...(entityInstance.notes ? { opacity: '1', color: this.colors.accent } : { opacity: '0.5' }),
          })}
          @click=${() =>
            ChromiumDialog.showPopup<void>({
              content: resolve => html`
                <x-flex padding="md" space="md">
                  <cr-textarea
                    placeholder="Notizen..."
                    .value=${entityInstance.notes}
                    @value-picked=${(e: ValueEvent<string>) => (entityInstance.notes = e.detail.value)}
                  ></cr-textarea>
                  <x-flex horizontal end space="md">
                    <cr-button mode="outline" @click=${() => resolve()}> Abbrechen </cr-button>
                    <cr-button
                      @click=${async () => {
                        try {
                          await ChromiumDialog.waitFor(
                            Promise.all([firstValueFrom(timer(250)), entityInstance.save()]),
                          );
                          void ChromiumSnackbar.show('Notiz aktualisiert');
                          resolve();
                        } catch (err) {
                          logger.error({ err }, 'Es gab ein Problem beim Speichern der Notizen');
                          void ChromiumDialog.alert(
                            'Es gab ein Problem beim Speichern der Notizen. Bitte versuchen Sie es später erneut!',
                          );
                        }
                      }}
                    >
                      Speichern
                    </cr-button>
                  </x-flex>
                </x-flex>
              `,
            })}
        >
          description
        </cr-icon>
      `,
  };

  private readonly statusIconColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: '',
    fieldName: 'status',
    columnWidth: 'min-content',
    isSortable: false,
    isAlwaysVisible: true,
    isSticky: true,
    position: 'front',
    renderItem: entityInstance => {
      return html`
        <cr-flex center>
          <cr-icon ${css({ fontSize: '1.2rem', color: g_getShippingStatusColor(entityInstance.status) })}>
            ${g_getShippingStatusIcon(entityInstance.status)}
            <cr-tooltip .text=${html`<cr-text>Status: ${entityInstance.status}</cr-text>`}></cr-tooltip>
          </cr-icon>
        </cr-flex>
      `;
    },
    filterAspect: ListAspectSchema(Object.values(LogistikShippingStatus)),
  };

  private readonly isExpressColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: '',
    fieldName: 'isExpress',
    columnWidth: 'min-content',
    isSortable: false,
    renderItem: entityInstance => {
      if (!entityInstance.isExpress) return nothing;
      return html`
        <cr-flex center>
          <cr-icon ${css({ fontSize: '1.2rem', color: this.colors.accent })}>
            bolt
            <cr-tooltip .text=${html`<cr-text> Expresslieferung angefragt </cr-text>`}></cr-tooltip>
          </cr-icon>
        </cr-flex>
      `;
    },
  };

  private readonly buttonColumn: ColumnDefinition<typeof LogistikOrder> = {
    customID: '_buttons',
    isSticky: true,
    position: 'back',
    isSortable: false,
    isAlwaysVisible: true,
    renderItem: entityInstance => {
      const editButton = (order: LogistikOrder) => html`
        <cr-button
          mode="icon"
          icon="edit"
          @click=${() => {
            void LogistikDialogEditOrder.showPopup({ props: { orderID: order._id } });
            this._selectedItems.clear();
            this.requestUpdate('_selectedItems', new Set());
          }}
        >
          <cr-tooltip> <cr-text> Bearbeiten </cr-text> </cr-tooltip>
        </cr-button>
      `;
      const emailButton = (order: LogistikOrder) => {
        const isEmailSent = order.emailsSent && order.emailsSent > 0;
        return html`
          <cr-button
            ${isEmailSent ? css({ color: this.colors.accent }) : null}
            mode="icon"
            icon="email"
            @click=${async () => {
              await LogistikDialogSendCustomerEmail.showPopup<boolean>({
                props: { logistikOrder: order },
              }).catch(() => undefined);
            }}
          >
            <cr-tooltip>
              ${isEmailSent
                ? html`<cr-text> ${order.emailsSent} E-Mail${order.emailsSent! > 1 ? 's' : ''} gesendet </cr-text>`
                : html`<cr-text> E-Mail senden </cr-text>`}
            </cr-tooltip>
          </cr-button>
        `;
      };
      const notificationButton = (order: LogistikOrder) => html`
        <cr-button
          mode="icon"
          icon="notifications"
          @click=${async () => {
            void LogistikDialogOrderReminder.showPopup({ props: { orderID: order._id } }).catch(() => {});
          }}
        >
          <cr-tooltip> <cr-text> Benachrichtigungen </cr-text> </cr-tooltip>
        </cr-button>
      `;
      const deleteButton = (order: LogistikOrder) => {
        if (this._currentUser.value?.hasPermission(ChancenPermission.logistik.DELETE_LOGISTIK_ORDER))
          return html`
            <cr-button
              mode="icon"
              icon="delete"
              @click=${async () => {
                const confirmation = await ChromiumDialog.confirm(
                  `Möchten Sie diese Bestellung wirklich unwiderruflich löschen?`,
                ).catch(() => false);
                if (!confirmation) return;
                const removalResult = await order.remove();
                if (removalResult) void ChromiumSnackbar.show('Bestellung erfolgreich gelöscht');
                else void ChromiumDialog.alert('Fehler beim Löschen der Bestellung');
              }}
            >
              <cr-tooltip> <cr-text> Löschen </cr-text> </cr-tooltip>
            </cr-button>
          `;
        else return nothing;
      };
      return html`
        <cr-flex horizontal center crossaxis-center space="sm">
          ${editButton(entityInstance)} ${emailButton(entityInstance)} ${notificationButton(entityInstance)}
          ${deleteButton(entityInstance)}
        </cr-flex>
      `;
    },
  };

  private readonly reviewActionsColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Freigabeaktionen',
    customID: 'freigabeaktionen',
    isSticky: true,
    position: 'front',
    isSortable: false,
    renderItem: (entityInstance: LogistikOrder) => {
      if (entityInstance.clearanceState !== LogistikOrderClearanceState.NEEDS_CLEARANCE) return nothing;
      return html`
        <cr-flex horizontal center crossaxis-center space="sm">
          <cr-button icon="check" tone="success" @click=${() => acceptIndividualOrder(entityInstance)}>
            Freigeben
          </cr-button>
          <cr-button icon="block" tone="error" @click=${() => declineIndividualOrder(entityInstance)}>
            Ablehnen
          </cr-button>
        </cr-flex>
      `;
    },
  };

  private readonly bestelldatumColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Bestelldatum',
    customID: 'bestelldatum',
    getFieldMapping: entityInstance => entityInstance.createdAt.toFormat('dd.MM.yyyy'),
  };

  private readonly firstColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Frist',
    customID: 'frist',
    getFieldMapping(entityInstance) {
      return entityInstance.shippingDetails.dueDate.toFormat('dd.MM.yyyy');
    },
  };

  private readonly clientColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Kund:in',
    customID: 'client',
    isSortable: false,
    getFieldMapping(entityInstance) {
      return entityInstance.shippingDetails.shippingSchool?.name ?? '-';
    },
    filterAspect: StringAspectConfig('contains'),
  };

  private readonly empfaengerColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Empfänger:in',
    customID: 'empfaenger',
    isSortable: false,
    getFieldMapping(entityInstance) {
      return entityInstance.shippingDetails.shippingRecipient;
    },
    filterAspect: ListAspectSchema(
      Object.values(LogistikShippingRecipient),
      undefined,
      ({ value }: { value: LogistikShippingRecipient | string }) => value,
    ),
  };

  private readonly bearbeiterColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Bearbeiter:in',
    customID: 'bearbeiter',
    getFieldMapping(entityInstance) {
      if (!entityInstance.processor) return '-';
      return (entityInstance.processor.vorname + ' ' + entityInstance.processor.nachname).trim();
    },
    filterAspect: ChancenwerkUserAspectSchema(
      new ChancenwerkUserFilter({
        aspects: [
          new ChancenRoleAspect({ value: Role.LOGISTICS_STAFF }),
          new BooleanAspect({ customID: 'active', value: true }),
        ],
      }),
    ),
  };

  private readonly auftraggeberColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Auftraggeber:in',
    customID: 'auftraggeber',
    getFieldMapping(entityInstance) {
      const name = entityInstance.shippingDetails.shippingRecipientName;
      const email = entityInstance.shippingDetails.customerEmail;
      if (name && email) return `${name} (${email})`;
      if (!email) return name;
      if (!name) return email;
      return '-';
    },
    filterAspect: StringAspectConfig('contains'),
  };

  private readonly isManuallyCreatedColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Ist manuelle Bestellung?',
    fieldName: 'isManuallyCreated',
    getFieldMapping(entityInstance) {
      return entityInstance.isManuallyCreated ? 'ja' : 'nein';
    },
  };

  private readonly productCategoryColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Produkte',
    customID: 'productCategory',
    isSortable: false,
    renderItem: entityInstance => {
      const products = Array.from(
        entityInstance.items.reduce((acc, item) => {
          acc.add(productDisplayName(item));
          return acc;
        }, new Set<string>()),
      )
        .sort()
        .join(', ');
      return html`
        <cr-flex>
          <cr-text ${css({ whiteSpace: 'nowrap' })}> ${ellipsis(products, 20)} </cr-text>
          <cr-tooltip> <cr-text> ${products} </cr-text> </cr-tooltip>
        </cr-flex>
      `;
    },
    filterAspect: ListAspectSchema(
      Object.values(LogistikProductCategory).map(c => c as string),
      undefined,
      ({ value }: { value: LogistikProductCategory | string }) => productCategoryDisplayName(value),
    ),
  };

  private readonly bestellnummerColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Bestellnummer',
    fieldName: 'orderNumber',
    getFieldMapping: entityInstance => displayLogistikOrderID(entityInstance),
    filterAspect: StringAspectConfig('contains'),
  };

  private readonly priceColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Preis',
    customID: 'price',
    isSortable: false,
    renderItem: (entityInstance: LogistikOrder) => {
      const item = entityInstance.items[0];
      if (!item || !(item instanceof LogistikIndividualOrderItem)) return html`<cr-text>-</cr-text>`;
      return html`
        <cr-flex center padding="xs">
          <cr-text>
            ${item.price && item.quantity
              ? Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(item.price * item.quantity)
              : '-'}
          </cr-text>
        </cr-flex>
      `;
    },
  };

  private readonly tokenColumn: ColumnDefinition<typeof LogistikOrder> = {
    columnName: 'Token',
    fieldName: 'accessToken',
    isSortable: false,
    filterAspect: StringAspectConfig('contains'),
  };

  private readonly _columnDefinitions: Array<ColumnDefinition<typeof LogistikOrder>> = [
    this.checkboxColumn,
    this.splitOrderColumn,
    this.emailKnownWarningColumn,
    this.notesColumn,
    this.isDigitalColumn,
    this.isSelfprintColumn,
    this.statusIconColumn,
    this.isExpressColumn,
    this.buttonColumn,
    this.reviewActionsColumn,
    this.bestelldatumColumn,
    this.firstColumn,
    this.clientColumn,
    this.empfaengerColumn,
    this.bearbeiterColumn,
    this.auftraggeberColumn,
    this.isManuallyCreatedColumn,
    this.productCategoryColumn,
    this.bestellnummerColumn,
    this.priceColumn,
    this.tokenColumn,
  ];
}

// region Selection Set
const orderSelector: BaseQLSelectionSet<LogistikOrder> = {
  _id: 1,
  _class: 1,
  createdAt: 1,
  orderNumber: 1,
  splitOrderIDs: 1,
  splitIndex: 1,
  status: 1,
  items: {
    _class: 1,
    _id: 1,
    productID: 1,
    quantity: 1,
    productCategory: 1,
    price: 1,
    link: 1,
    acceptAlternative: 1,
    isDigital: 1,
    isSelfPrint: 1,
  },
  emailsSent: 1,
  isManuallyCreated: 1,
  processorID: 1,
  deliveryStatus: LogistikOrderDeliveryState.allFields,
  shippingMethod: 1,
  trackingLink: 1,
  processor: {
    _id: 1,
    _class: 1,
    email: 1,
    username: 1,
    vorname: 1,
    nachname: 1,
  },
  shippingDetails: {
    _class: 1,
    dueDate: 1,
    customerEmail: 1,
    shippingRecipient: 1,
    shippingRecipientName: 1,
    shippingSchoolID: 1,
    isEmailKnown: 1,
    shippingInstructions: 1,
    shippingAddress: Adresse.allFields,
    shippingSchool: {
      _id: 1,
      _class: 1,
      name: 1,
      kuerzel: 1,
      lastAdvertisingMaterialOrderDate: 1,
      lastLearningMaterialOrderDate: 1,
      address: Adresse.allFields,
      strasse: 1,
      country: 1,
      standort: 1,
    },
  },
  splitFrom: 1,
  mergeOriginalOrders: { _id: 1 },
  notes: 1,
  accessTokenValid: 1,
  accessToken: 1,
  isExpress: 1,
  sourceService: 1,
  clearanceState: 1,
  isDigital: 1,
};

// region Helpers
function productDisplayName(
  item: LogistikIndividualOrderItem | LogistikEntireTrip | LogistikOtherOrder | LogistikOrderItem,
): string {
  if (item instanceof LogistikIndividualOrderItem) return 'Individuelle Bestellung';
  if (item instanceof LogistikEntireTrip) return 'Fahrt und Unterkunft';
  if (item instanceof LogistikOtherOrder) return 'Sonstige';
  return productCategoryDisplayName(item.productCategory);
}

function productCategoryDisplayName(category: LogistikProductCategory | string): string {
  switch (category) {
    case LearningMaterial._class:
    case LogistikProductCategory.LEARNING_MATERIAL:
      return 'CHANCENhefte';
    case AdvertisingMaterial._class:
    case LogistikProductCategory.ADVERTISING_MATERIAL:
      return 'Werbe- und Akquisematerial';
    case ChancenBrief._class:
    case LogistikProductCategory.CHANCEN_BRIEF:
      return 'CHANCENbrief';
    case LogistikEntireTrip._class:
      return 'Fahrt und Unterkunft';
    case LogistikOtherOrder._class:
      return 'Sonstige';
    case LogistikIndividualOrderItem._class:
    case LogistikProductCategory.FURNISHING:
    case LogistikProductCategory.INDIVIDUAL:
    case LogistikProductCategory.TICKETS:
    case LogistikProductCategory.WORKSHOP_PACK:
    case LogistikProductCategory.WORK_MATERIAL:
    case LogistikProductCategory.SET:
    case LogistikProductCategory.TRAVEL_CERTIFICATE:
    case LogistikProductCategory.OTHER:
    case LogistikProductCategory.ENTIRE_TRIP:
      return category;
    default:
      return g_getProductCategoryString(category);
  }
}
